import React, { Component } from 'react';
import { Button, Intent } from '@blueprintjs/core';
import { observer, inject } from 'mobx-react';
import Toast from 'helpers/toaster';
import { FormSection, Choice } from 'components/FormBits';
import { SettingsCard } from 'components/SettingsPage';
import { CurrentEnv } from '../../services/Api';

// The online payment providers customers can pay invoices through.
const PROVIDERS = [
  { value: 'stripe_connect', name: 'Stripe', logo: '/stripe-logo.svg', fee: '2.9% + $0.30 per payment' },
  { value: 'bambora', name: 'Bambora', logo: '/bambora-logo-mark.svg', fee: '2.9% + $0.30 per payment' },
];

const BAMBORA_ENVIRONMENTS = [
  { value: 'development', label: 'Development' },
  { value: 'production', label: 'Production' },
];

// Settings > Payment settings (pages/preferences/index.js): which provider
// takes online payments (and its connection), and when invoices are due.
@inject('preferenceStore', 'viewStore') @observer
class PreferencesProfileCard extends Component {

  componentDidMount() {
    const { preference } = this.props;
    if (!preference.custom_bambora_env) {
      preference.custom_bambora_env = 'development';
    }
  }

  set(key, value) {
    this.props.preference[key] = value;
  }

  stripeConnectLink = () => {
    const { tenant_id, api_token } = this.props.viewStore.currentUser.attributes;
    return `${CurrentEnv.domain()}/auth/stripe_connect_redirect/?tenant_id=${tenant_id}&api_token=${api_token}`;
  }

  save = () => {
    this.props.preferenceStore.updatePreference()
      .then(() => {
        Toast.show({ message: 'Payment settings saved.', intent: Intent.SUCCESS });
      })
      .catch((error) => {
        const data = error && error.response && error.response.data;
        const reason = data && (data.error || data.errors);
        Toast.show({
          message: reason ? (Array.isArray(reason) ? reason.join(', ') : reason) : "Couldn't save the payment settings. Try again.",
          intent: Intent.DANGER,
        });
      });
  }

  textField(key, label, { type = 'text', placeholder, autoComplete = 'off' } = {}) {
    const id = `payment-${key.replace(/_/g, '-')}`;
    return (
      <div className="bp3-form-group">
        <label className="fb-label" htmlFor={id}>{label}</label>
        <input
          id={id}
          type={type}
          className="bp3-input bp3-fill"
          placeholder={placeholder}
          autoComplete={autoComplete}
          value={this.props.preference[key] || ''}
          onChange={(event) => this.set(key, event.target.value)}
        />
      </div>
    );
  }

  renderProviders() {
    const { preference } = this.props;
    return (
      <div className="pay-providers" role="radiogroup" aria-label="Payment provider">
        {PROVIDERS.map((provider) => {
          const selected = preference.payment_gateway === provider.value;
          return (
            <button
              key={provider.value}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`pay-provider${selected ? ' is-selected' : ''}`}
              onClick={() => this.set('payment_gateway', provider.value)}
            >
              <span className="pay-provider__logo"><img src={provider.logo} alt="" /></span>
              <span className="pay-provider__text">
                <span className="pay-provider__name">{provider.name}</span>
                <span className="pay-provider__fee">{provider.fee}</span>
              </span>
              <span className="pay-provider__radio" aria-hidden="true" />
            </button>
          );
        })}
      </div>
    );
  }

  renderStripe() {
    const info = this.props.preference.stripe_checkout_info;
    if (!info) {
      return (
        <div className="pay-connect">
          <p className="pay-connect__text">Connect your Stripe account so customers can pay invoices by card.</p>
          <a className="bp3-button sp-button sp-button--primary" href={this.stripeConnectLink()}>
            Connect with Stripe
          </a>
        </div>
      );
    }
    return (
      <div className="pay-account">
        <div className="pay-account__head">
          <span className="sp-pill sp-pill--green">Connected</span>
          <span className={`sp-pill${info.livemode ? '' : ' sp-pill--amber'}`}>{info.livemode ? 'Live payments' : 'Test mode'}</span>
        </div>
        <dl className="pay-account__details">
          {info.name && (<><dt>Account</dt><dd>{info.name}</dd></>)}
          {info.email && (<><dt>Email</dt><dd>{info.email}</dd></>)}
          {info.nickname && (<><dt>Nickname</dt><dd>{info.nickname}</dd></>)}
          {info.scope && (<><dt>Access</dt><dd>{info.scope}</dd></>)}
        </dl>
        <a className="pay-account__reconnect" href={this.stripeConnectLink()}>Reconnect Stripe</a>
      </div>
    );
  }

  renderBambora() {
    const { preference } = this.props;
    return (
      <React.Fragment>
        <Choice
          label="Environment"
          name="Bambora environment"
          value={preference.custom_bambora_env}
          onChange={(value) => this.set('custom_bambora_env', value)}
          options={BAMBORA_ENVIRONMENTS}
        />
        <div className="fb-grid">
          {this.textField('bambora_dev_merchant_id', 'Development merchant ID')}
          {this.textField('bambora_dev_api_key', 'Development API key', { type: 'password', autoComplete: 'new-password' })}
          {this.textField('bambora_pro_merchant_id', 'Production merchant ID')}
          {this.textField('bambora_pro_api_key', 'Production API key', { type: 'password', autoComplete: 'new-password' })}
        </div>
      </React.Fragment>
    );
  }

  render() {
    const { preference, preferenceStore } = this.props;
    const saving = preferenceStore.isSavingPreference;
    return (
      <SettingsCard
        footer={<Button className="sp-button sp-button--primary" icon="floppy-disk" text="Save changes" loading={saving} onClick={this.save} />}
      >
        <FormSection title="Online payments" hint="Customers pay your invoices online through this provider.">
          {this.renderProviders()}
          {preference.payment_gateway === 'stripe_connect' && this.renderStripe()}
          {preference.payment_gateway === 'bambora' && this.renderBambora()}
        </FormSection>

        <FormSection title="Invoices" hint="When payment is due, unless a client has their own terms.">
          <div className="bp3-form-group pay-due">
            <label className="fb-label" htmlFor="payment-invoice-due">Due within</label>
            <div className="pay-due__field">
              <input
                id="payment-invoice-due"
                type="number"
                min="0"
                inputMode="numeric"
                className="bp3-input"
                value={preference.invoice_due_within_days || ''}
                onChange={(event) => this.set('invoice_due_within_days', event.target.value)}
              />
              <span>days</span>
            </div>
          </div>
        </FormSection>
      </SettingsCard>
    );
  }
}

export default PreferencesProfileCard;
